import { useEffect, useRef } from 'react';
import { HandDetector, LandmarkSmoother, isIOS, palmCenter, palmWidth } from '@engine/hand';
import { useHandStore, type HandId, type HandSnapshot, type Landmarks } from '@store/index';
import { usePerfStore } from '@store/perfStore';

export interface HandFrameCallback {
  (ts: number): void;
}

interface VideoFrameCapableElement extends HTMLVideoElement {
  requestVideoFrameCallback?: (
    cb: (now: number, metadata: { mediaTime: number }) => void,
  ) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
}

/**
 * Runs MediaPipe HandLandmarker against a <video> element.
 *
 * Only initializes when `enabled` is true — we defer MediaPipe bootstrap
 * until the user has explicitly tapped to start (same gesture stack that
 * unlocks audio).
 */
export function useHandDetection(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  enabled: boolean,
  onFrame?: HandFrameCallback,
) {
  const setDetecting = useHandStore((s) => s.setDetecting);
  const updateHand = useHandStore((s) => s.updateHand);
  const resetHand = useHandStore((s) => s.resetHand);
  const setFps = usePerfStore((s) => s.setFps);

  const detectorRef = useRef<HandDetector | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let raf = 0;
    let rvfc = 0;
    let frames = 0;
    let fpsWindowStart = performance.now();
    let lastFpsUpdate = fpsWindowStart;

    const smoothers: Record<HandId, LandmarkSmoother> = {
      left: new LandmarkSmoother(),
      right: new LandmarkSmoother(),
    };

    const detector = new HandDetector();
    detectorRef.current = detector;

    const processFrame = (videoTimestampMs: number, wallNow: number) => {
      if (document.hidden) return;
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;

      const frame = detector.detect(video, videoTimestampMs);
      if (!frame) return;

      const { result } = frame;
      const presentHands = new Set<HandId>();
      const handedness = result.handedness ?? [];
      const landmarksArr = result.landmarks ?? [];

      for (let i = 0; i < landmarksArr.length; i++) {
        const rawLm = landmarksArr[i]!;
        const categoryName = handedness[i]?.[0]?.categoryName ?? 'Right';
        // NOTE: MediaPipe's Left/Right label is from the un-mirrored
        // camera's perspective, so under `mirrorCamera=true` a HandId
        // of 'left' here actually carries the user's PHYSICAL right
        // hand. We intentionally keep the mapping straight-through so
        // the same physical hand always routes to the same detector,
        // smoother and bucket — swapping per-frame caused handedness
        // flicker to split one physical hand across both detectors,
        // producing alternating-colour ghost events. Consumers that
        // care about the user's actual left/right (glow colour) apply
        // their own perspective correction locally.
        const id: HandId = categoryName === 'Left' ? 'left' : 'right';
        presentHands.add(id);

        const smoother = smoothers[id];
        const smoothed: Landmarks = smoother.push(rawLm);
        const center = palmCenter(smoothed);
        const width = palmWidth(smoothed);
        const snap: HandSnapshot = {
          id,
          present: true,
          landmarks: smoothed,
          palm: center,
          palmWidth: width,
          confidence: handedness[i]?.[0]?.score ?? 1,
          updatedAt: wallNow,
        };
        updateHand(snap);
      }

      if (!presentHands.has('left')) {
        smoothers.left.reset();
        resetHand('left');
      }
      if (!presentHands.has('right')) {
        smoothers.right.reset();
        resetHand('right');
      }

      try {
        onFrame?.(wallNow);
      } catch (err) {
        console.error('[hand-detection] gesture step failed', err);
      }

      frames += 1;
      if (wallNow - lastFpsUpdate >= 500) {
        setFps((frames * 1000) / (wallNow - fpsWindowStart));
        if (wallNow - fpsWindowStart >= 2000) {
          frames = 0;
          fpsWindowStart = wallNow;
        }
        lastFpsUpdate = wallNow;
      }
    };

    const rvfcLoop = (now: number, metadata: { mediaTime: number }) => {
      if (cancelled) return;
      processFrame(metadata.mediaTime * 1000, now);
      const video = videoRef.current as VideoFrameCapableElement | null;
      if (video?.requestVideoFrameCallback) {
        rvfc = video.requestVideoFrameCallback(rvfcLoop);
      }
    };

    const rafLoop = () => {
      if (cancelled) return;
      const now = performance.now();
      processFrame(now, now);
      raf = requestAnimationFrame(rafLoop);
    };

    const boot = async () => {
      await detector.initialize();
      if (cancelled) return;
      if (detector.lastError || !detector.isReady()) {
        useHandStore.setState({
          cameraError: `MediaPipe init failed: ${detector.lastError ?? 'unknown'}`,
        });
        return;
      }
      setDetecting(true);
      const video = videoRef.current as VideoFrameCapableElement | null;
      // iOS Safari's requestVideoFrameCallback is flaky with MediaStream
      // sources — force rAF on iOS for reliability.
      const useRvfc = !isIOS() && !!video?.requestVideoFrameCallback;
      if (useRvfc && video?.requestVideoFrameCallback) {
        rvfc = video.requestVideoFrameCallback(rvfcLoop);
      } else {
        raf = requestAnimationFrame(rafLoop);
      }
    };

    void boot();

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      const video = videoRef.current as VideoFrameCapableElement | null;
      if (rvfc && video?.cancelVideoFrameCallback) {
        video.cancelVideoFrameCallback(rvfc);
      }
      detector.dispose();
      detectorRef.current = null;
      setDetecting(false);
      setFps(0);
    };
  }, [enabled, videoRef, onFrame, setDetecting, updateHand, resetHand, setFps]);
}
